import type {ReactElement, ReactNode} from 'react';
import {useEffect, useState} from 'react';
import {ControlledListItemType7} from '@Components/controlled-list/components/controlled-list-item-type-7';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {MODALS_IDS} from '@Components/modal-container';
import type {MoreOptionsModalProps} from '@Modals/more-options-modal/more-options-modal';
import {useAppSelector} from '@/hooks';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {ActionMenu} from '@Components/action-menu/action-menu';
import {ActionMenuFlyout} from '@Components/action-menu/components/action-menu-flyout/action-menu-flyout';
import type {ActionMenuItem} from '@Components/action-menu/action-menu.types';
import {EDITABLE_LAYERS_BADGE_COUNT, EditMenuScreen} from './useImageEditMenu.types';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useOpenChatImageInEditor} from '@Components/ai-chat-window/hooks/useOpenChatImageInEditor';
import {useMakeTextEditable} from '@Components/ai-chat-window/hooks/useMakeTextEditable';
import {EDITABLE_LAYERS_TEXT_REMOVAL_METHOD} from '@Components/ai-chat-window/lib/make-text-editable.library';
import {LoadingStates} from '@Utils/loading.util';
import styles from './useImageEditMenu.module.scss';

interface UseImageEditMenuParams {
  chatId: string;
  hashedId: string;
  hasLayeredPoster: boolean;
  moreOptionsPanelId: string;
  isDisabled: boolean;
}

interface UseImageEditMenu {
  editScreen: EditMenuScreen;
  isEditSubmenuScreen: boolean;
  isEditMenuInModalMode: boolean;
  showEditableLayers: boolean;
  getMobileEditMenuRow: () => ReactElement;
  getEditMenuRow: () => ReactElement;
  getEditSubmenu: (isMobile?: boolean) => ReactElement;
  getMoreOptionsModalHeaderProps: () => Partial<MoreOptionsModalProps>;
}

export const useImageEditMenu = ({chatId, hashedId, hasLayeredPoster, moreOptionsPanelId, isDisabled}: UseImageEditMenuParams): UseImageEditMenu => {
  const [editScreen, setEditScreen] = useState<EditMenuScreen>(EditMenuScreen.MAIN);
  const {windowWidth} = useWindowSize();
  const {state} = useAiChat();
  const {openPosterInEditor} = useOpenChatImageInEditor();
  const {makeTextEditable} = useMakeTextEditable({chatId});
  const isEditMenuInModalMode = windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;
  const showEditableLayers = state.canUseEditableTextLayers || (state.isSuperUser && hasLayeredPoster);

  const loadingStates = state.getLoadingStatesForImageActions(chatId, hashedId);
  const isOpenEditorLoading = loadingStates.openEditorLoadingState === LoadingStates.LOADING;
  const isMakeEditableLoading = loadingStates.makeTextEditableLoadingState === LoadingStates.LOADING;

  const onOpenInEditor = (): void => {
    void openPosterInEditor(chatId, hashedId);
  };

  const onMakeEditableLayers = (): void => {
    void makeTextEditable(hashedId, EDITABLE_LAYERS_TEXT_REMOVAL_METHOD, hasLayeredPoster);
  };

  const isMoreOptionsModalOpen = useAppSelector((reduxState): boolean => {
    const modal = reduxState.modals.modalsHashmap[MODALS_IDS.MORE_OPTIONS_MODAL];
    if (!modal?.isOpen) {
      return false;
    }
    return modal.panelProps?.panelId === moreOptionsPanelId;
  });

  useEffect(() => {
    if (!isMoreOptionsModalOpen) {
      setEditScreen(EditMenuScreen.MAIN);
    }
  }, [isMoreOptionsModalOpen]);

  const getEditableLayersBadge = (): ReactElement => {
    return (
      <span className={styles.badge}>
        <Icon icon="icon-credits" size={IconSize.SIZE_ICON_12} className="ai-text-dark" />
        <Text size={TextSize.SM} bold val={EDITABLE_LAYERS_BADGE_COUNT} className={`ai-text-dark ${styles.badgeCount}`} />
      </span>
    );
  };

  const getEditableLayersInfoNote = (): ReactNode => {
    if (!showEditableLayers) {
      return undefined;
    }
    return (
      <div className={styles.infoNote}>
        <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} className={styles.infoIcon} />
        <Text size={TextSize.SM} val={window.i18next.t('pmwjs_editable_layers_info')} className={styles.infoText} />
      </div>
    );
  };

  const getEditMenuItems = (): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'edit-as-image',
        icon: 'icon-image',
        text: window.i18next.t('pmwjs_edit_as_image'),
        onClick: onOpenInEditor,
        isLoading: isOpenEditorLoading,
        isDisabled,
      },
    ];

    if (showEditableLayers) {
      items.push({
        id: 'editable-layers',
        icon: 'icon-prints-stack',
        text: window.i18next.t('pmwjs_editable_layers'),
        onClick: onMakeEditableLayers,
        isLoading: isMakeEditableLoading,
        isDisabled,
        customElement: isMakeEditableLoading ? undefined : getEditableLayersBadge(),
      });
    }

    return items;
  };

  const getMobileEditMenuRow = (): ReactElement => {
    return (
      <ControlledListItemType7
        id={'edit'}
        text={window.i18next.t('pmwjs_edit')}
        icon={'icon-pencil'}
        rightIcon={'icon-caret-right'}
        onClick={(_id, event): void => {
          event?.stopPropagation();
          setEditScreen(EditMenuScreen.EDIT);
        }}
        isDisabled={isDisabled}
        className={'radius-8'}
      />
    );
  };

  const getEditMenuRow = (): ReactElement => {
    if (isEditMenuInModalMode) {
      return getMobileEditMenuRow();
    }

    return (
      <ActionMenuFlyout
        items={getEditMenuItems()}
        infoNote={getEditableLayersInfoNote()}
        isDisabled={isDisabled}
        triggerText={window.i18next.t('pmwjs_edit')}
        triggerIcon="icon-pencil"
      />
    );
  };

  const getEditSubmenu = (isMobile = true): ReactElement => {
    return <ActionMenu isMobile={isMobile} items={getEditMenuItems()} infoNote={getEditableLayersInfoNote()} />;
  };

  const getMoreOptionsModalHeaderProps = (): Partial<MoreOptionsModalProps> => {
    if (editScreen === EditMenuScreen.EDIT) {
      return {
        headerTitle: window.i18next.t('pmwjs_edit'),
        showBackButton: true,
        showCloseButton: false,
        closeModalOnBack: false,
        shouldCenterAlignTitle: true,
        onBack: (): void => {
          setEditScreen(EditMenuScreen.MAIN);
        },
      };
    }

    return {
      headerTitle: window.i18next.t('pmwjs_more_options'),
      showBackButton: false,
      showCloseButton: true,
      closeModalOnBack: true,
    };
  };

  return {
    editScreen,
    isEditSubmenuScreen: editScreen === EditMenuScreen.EDIT,
    isEditMenuInModalMode,
    showEditableLayers,
    getMobileEditMenuRow,
    getEditMenuRow,
    getEditSubmenu,
    getMoreOptionsModalHeaderProps,
  };
};
